import React, {ReactNode, useMemo} from "react";
import {
    Alert,
    Box,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Divider,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import {Masonry} from "@mui/lab";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeskIcon from "@mui/icons-material/TableRestaurant";
import UndoIcon from "@mui/icons-material/Clear";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import EditIcon from "@mui/icons-material/Edit";
import {
    DeskRef,
    Neighbourhood,
    SeatingPlanNewNeighbourhood,
    SeatingPlanNewNeighbourhoodForm,
    UNASSIGNED_NEIGHBOURHOOD_KEY,
} from "../../api/Model";
import NeighbourhoodColorIcon from "../../components/neighbourhood-icon/NeighbourhoodColorIcon";
import EmployeeAvatar from "../../components/employee-avatar/EmployeeAvatar";
import EmployeeLink from "../../components/employee-link/EmployeeLink";
import DeskChip from "../../components/desk-chip/DeskChip";
import {HoodItem, SeatingPlanModel} from "./SeatingPlanModel";
import {newNeighbourhoodAction} from "./actions/CreateNewSeatingPlanNeighbourhoodMenuItem";
import {editPlanAction} from "./actions/EditSeatingPlanButton";
import {applyPlanAction} from "./actions/ApplySeatingPlanButton";
import {SeatingPlanTab} from "./SeatingPlanTab";
import {usePermission} from "../../util/access/UseAccess";
import {SeatingPlans_Admin} from "../../util/access/Permission";
import {recordKeys} from "../../util/ObjectUtil";
import {mapNotNull} from "../../util/ArrayUtil";
import {associateBy} from "../../util/MapUtil";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {Opt} from "../../util/Optional";
import {ConfirmOptions, ConfirmResult, useConfirm} from "material-ui-confirm";
import {Message, useMessage} from "../../util/actions/Message";
import {projection} from "../../util/ProjectionUtil";
import {numberCmp} from "../../util/SortUtil";
import {SeatingPlanTabLabel} from "./SeatingPlanTabLabel";

function RollbackButton({title, onClick}: { title: string, onClick: () => void }) {
    return (
        <Tooltip title={title}>
            <IconButton size="small" onClick={onClick} aria-label={title}>
                <UndoIcon fontSize="small"/>
            </IconButton>
        </Tooltip>
    );
}

function ChangeRow({icon, children, revert}: {
    icon: React.ReactNode,
    children: React.ReactNode,
    revert: Change['revert'],
}) {
    return (
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box color="text.secondary" sx={{display: 'flex', pt: 0.25}}>{icon}</Box>
            <Box flexGrow={1} minWidth={0}>{children}</Box>
            {revert && <RollbackButton title={revert.title} onClick={revert.onRevert}/>}
        </Stack>
    );
}

function ChangeDescription({type, children}: {
    type?: 'add' | 'remove' | 'change'
    children: ReactNode,
}) {
    return (
        <Typography
            variant="caption"
            color="textSecondary"
            sx={{overflowWrap: 'anywhere'}}
            display="block"
        >
            <Stack component="span" direction="row" gap="4px" alignItems="center">
                {type === 'add' && <AddCircleIcon fontSize="inherit" color="inherit"/>}
                {type === 'remove' && <RemoveCircleIcon fontSize="inherit" color="inherit"/>}
                {type === 'change' && <EditIcon fontSize="inherit" color="inherit"/>}
                {children}
            </Stack>
        </Typography>
    );
}

type Change = {
    key: string,
    icon: ReactNode,
    revert?: {
        title: string,
        onRevert: () => void,
    }
    content: ReactNode,
};

class RevertContext {
    constructor(
        private readonly message: Message,
        private readonly confirm: (options?: ConfirmOptions) => Promise<ConfirmResult>,
        private readonly canAdmin: boolean,
    ) {
    }

    revert({title, description, action}: {
        title: string,
        description: string,
        action: (
            onSuccess: Message['success'],
            onFailure: Message['error'],
        ) => Promise<void>,
    }): Change['revert'] {
        if (!this.canAdmin) {
            return undefined;
        }

        return {
            title: title,
            onRevert: async () => {
                const {confirmed} = await this.confirm({
                    title: title,
                    description,
                    confirmationText: 'Revert',
                    confirmationButtonProps: {
                        color: 'error',
                        variant: 'contained',
                    },
                });
                if (confirmed) {
                    await action(
                        this.message.success.bind(this.message),
                        this.message.error.bind(this.message),
                    );
                }
            },
        };
    }

}

const hoodPropLabels: Record<keyof SeatingPlanNewNeighbourhoodForm, {
    label: string,
    render: (value: Opt<string>) => ReactNode
}> = {
    'name': {
        label: 'Renaming',
        render: (value) => value ?? '<empty>',
    },
    'displayColor': {
        label: 'Changing color',
        render: (value) => value ? (
            <Box sx={{
                backgroundColor: value,
                color: theme => theme.palette.getContrastText(value),
                px: 0.5,
                borderRadius: 1
            }}>{value}</Box>
        ) : 'empty',
    },
    'notes': {
        label: 'Updating notes',
        render: (value) => value ?? '<empty>',
    },
    'slackChannelId': {
        label: 'Changing slack channel',
        render: (value) => value ?? '<empty>',
    },
};

function collectPropertyChanges(
    model: SeatingPlanModel,
    hood: Neighbourhood,
    originalProps: SeatingPlanNewNeighbourhoodForm,
    revertContext: RevertContext,
): Change[] {
    const propNames = recordKeys(hoodPropLabels);
    return propNames
        .map(propertyName => ({
            propertyName,
            before: originalProps[propertyName],
            after: hood[propertyName],
        }))
        .filter(({before, after}) => before !== after)
        .map(({propertyName, before, after}) => ({
            key: `property-change-${propertyName}`,
            icon: <EditOutlinedIcon fontSize="small"/>,
            content: (
                <>
                    <Typography
                        variant="body1"
                        display="block"
                    >
                        {hoodPropLabels[propertyName].label}
                    </Typography>
                    <ChangeDescription>
                        {hoodPropLabels[propertyName].render(before)} <ArrowForwardIcon fontSize="inherit"
                                                                                        color="inherit"/> {hoodPropLabels[propertyName].render(after)}
                    </ChangeDescription>
                </>
            ),
            revert: revertContext.revert({
                title: `Revert ${hoodPropLabels[propertyName].label}`,
                description: `Are you sure you want to revert the property back to "${before}"`,
                action: async (onSuccess, onFailure) => {
                    try {
                        const patch = {
                            ...projection(hood, propNames),
                            [propertyName]: before,
                        };
                        await model.setNeighbourhoodUpdate(
                            hood,
                            propNames.every(p => patch[p] === originalProps[p])
                                ? undefined
                                : patch,
                        );
                        onSuccess(`Reverted ${hoodPropLabels[propertyName].label} back to "${before}"`);
                    } catch (e) {
                        onFailure(`Failed to revert the ${hoodPropLabels[propertyName].label}`, e);
                    }
                },
            }),
        }));
}

function collectEmployeeChanges(
    model: SeatingPlanModel,
    hood: HoodItem,
    hoodsByKey: Map<HoodItem['key'], HoodItem>,
    revertContext: RevertContext,
): Change[] {
    const hoodEmployeeMoves = model.hoodKey2employeeId2move.get(hood.key);
    if (!hoodEmployeeMoves) {
        return [];
    } else {
        return [...hoodEmployeeMoves.values()]
            .map(move => ({
                move,
                fromHoods: mapNotNull(
                    model.employeeId2moves
                        .get(move.employee.id)
                        ?.filter(otherMove =>
                            (SeatingPlanModel.hoodRefKey(otherMove.neighbourhood) !== SeatingPlanModel.hoodRefKey(move.neighbourhood)) &&
                            otherMove.operation !== move.operation
                        ) ?? [],
                    otherMove => hoodsByKey.get(SeatingPlanModel.hoodRefKey(otherMove.neighbourhood)),
                )
            }))
            .map(({move, fromHoods}) => ({
                key: `employee-move-${move.employee.id}`,
                icon: <EmployeeAvatar employee={move.employee} sx={{width: 24, height: 24}}/>,
                content: (
                    <>
                        <EmployeeLink employee={move.employee}/>
                        {move.operation === 'add' ? (
                            <ChangeDescription type="add">
                                joining
                                {fromHoods.length > 0 && (
                                    ` from ${fromHoods.map(fromHood => fromHood.name).join(', ')}`
                                )}
                            </ChangeDescription>
                        ) : (
                            <ChangeDescription type="remove">
                                leaving
                                {fromHoods.length > 0 && (
                                    ` to ${fromHoods.map(fromHood => fromHood.name).join(', ')}`
                                )}
                            </ChangeDescription>
                        )}
                    </>
                ),
                revert: revertContext.revert({
                    title: 'Revert employee move',
                    description: 'Are you sure you want to revert this employee move?',
                    action: async (onSuccess, onFailure) => {
                        try {
                            await model.revertEmployeeMove(move);
                            onSuccess('Employee move reverted');
                        } catch (e) {
                            onFailure('Failed to revert employee move', e);
                        }
                    },
                }),
            }));
    }
}

function collectDeskChanges(model: SeatingPlanModel, hood: HoodItem, revertContext: RevertContext): Change[] {

    function revert(desk: DeskRef) {
        return revertContext.revert({
            title: 'Revert desk changes',
            description: 'Are you sure you want to revert all planned changes to this desk?',
            action: async (onSuccess, onFailure) => {
                try {
                    await model.revertDeskAssignment(desk);
                    onSuccess(`Desk ${desk.name} changes reverted`);
                } catch (e) {
                    onFailure(`Failed to revert desk ${desk.name} changes`, e);
                }
            },
        });
    }

    return [
        ...hood.oldDesks
            .map(desk => ({
                desk,
                newHood: model.newDeskAssignment(desk).neighbourhood,
                newEmployee: model.newDeskAssignment(desk).employee,
                oldEmployee: model.oldDeskAssignment(desk).employee,
            }))
            .filter(({newHood}) => (newHood?.key ?? UNASSIGNED_NEIGHBOURHOOD_KEY) !== hood.key)
            .map(({desk, newHood, newEmployee, oldEmployee}) => ({
                key: `desk-removed-${desk.id}`,
                icon: <DeskIcon fontSize="small"/>,
                content: (
                    <>
                        <DeskChip desk={desk} deskAssignee={oldEmployee} size="small"/>
                        <ChangeDescription type="remove">
                            {newHood ? (
                                `moving to ${newHood.name}`
                            ) : (
                                `removing from neighbourhood`
                            )}
                        </ChangeDescription>
                        {oldEmployee && oldEmployee.id !== newEmployee?.id && (
                            <ChangeDescription>
                                unassigning from {oldEmployee.fullName}
                            </ChangeDescription>
                        )}
                    </>
                ),
                revert: revert(desk),
            })),
        ...hood.newDesks
            .map(desk => ({
                desk,
                oldHood: model.oldDeskAssignment(desk).neighbourhood,
                oldEmployee: model.oldDeskAssignment(desk).employee,
                newEmployee: model.newDeskAssignment(desk).employee,
            }))
            .filter(({oldHood, oldEmployee, newEmployee}) =>
                (oldHood?.key ?? UNASSIGNED_NEIGHBOURHOOD_KEY) !== hood.key || oldEmployee?.id !== newEmployee?.id
            )
            .map(({desk, oldHood, oldEmployee, newEmployee}) => ({
                key: `desk-added-${desk.id}`,
                icon: <DeskIcon fontSize="small"/>,
                content: (
                    <>
                        <DeskChip desk={desk} deskAssignee={newEmployee} size="small"/>
                        {hood?.key !== (oldHood?.key ?? UNASSIGNED_NEIGHBOURHOOD_KEY) && (
                            <ChangeDescription type="add">{oldHood ? (
                                `moving from ${oldHood.name}`
                            ) : (
                                `adding to neighbourhood`
                            )}</ChangeDescription>
                        )}
                        {oldEmployee?.id !== newEmployee?.id && (
                            (oldHood?.key ?? UNASSIGNED_NEIGHBOURHOOD_KEY) === hood.key ? (
                                <ChangeDescription type="change">{oldEmployee && newEmployee ? (
                                    `re-assigning to ${newEmployee.fullName} from ${oldEmployee.fullName}`
                                ) : newEmployee ? (
                                    `assigning to ${newEmployee.fullName}`
                                ) : oldEmployee ? (
                                    `unassigning from ${oldEmployee.fullName}`
                                ) : ''
                                }</ChangeDescription>
                            ) : (
                                <ChangeDescription>{newEmployee ? (
                                    `assigning to ${newEmployee.fullName}`
                                ) : (
                                    ''
                                )}</ChangeDescription>
                            )
                        )}
                    </>
                ),
                revert: revert(desk),
            })),
    ];
}

function collectNewNeighbourhoodChange(model: SeatingPlanModel, hood: SeatingPlanNewNeighbourhood, revertContext: RevertContext): Change {
    return {
        key: `neighbourhood-created-${hood.key}`,
        icon: <NeighbourhoodColorIcon neighbourhood={hood} size={20}/>,
        content: (
            <>
                <Typography variant="body1">Creating New Neighbourhood</Typography>
            </>
        ),
        revert: revertContext.revert({
            title: `Revert creating new neighbourhood ${hood.name}`,
            description: 'Are you sure you want to revert this new neighbourhood?',
            action: async (onSuccess, onFailure) => {
                try {
                    await model.revertNewNeighbourhood(hood);
                    onSuccess(`Reverted creating new neighbourhood ${hood.name}`);
                } catch (e) {
                    onFailure('Failed to revert creating new neighbourhood', e);
                }
            },
        }),
    };
}

function collectChanges(
    model: SeatingPlanModel,
    revertContext: RevertContext,
): { hood: HoodItem, changes: Change[] }[] {

    const hoodsByKey = associateBy(
        model.hoodItems,
        hood => hood.key,
    );

    return model.hoodItems
        .map(hood => {
            const propertyChanges = hood.type === 'existing' && hood.originalProps
                ? collectPropertyChanges(model, hood, hood.originalProps, revertContext)
                : [];
            const employeeChanges = collectEmployeeChanges(model, hood, hoodsByKey, revertContext);
            const deskChanges = collectDeskChanges(model, hood, revertContext);

            return {
                hood,
                changes: [
                    ...propertyChanges,
                    ...employeeChanges,
                    ...deskChanges,
                ],
            };
        })
        .map(({hood, changes}) => ({
            hood,
            changes: changes.length === 0 && hood.type === 'new' ? (
                [
                    collectNewNeighbourhoodChange(model, hood, revertContext)
                ]
            ) : changes,
        }))
        .filter(({changes}) => changes.length > 0)
        .sort(numberCmp(({changes}) => changes.length, true));
}

function NeighbourhoodCard(
    {
        hood,
        changes,
    }: {
        hood: HoodItem,
        changes: Change[],
        canAdmin: boolean,
    }
) {
    return (
        <Card variant="outlined">
            <CardHeader
                avatar={<NeighbourhoodColorIcon neighbourhood={hood}/>}
                title={<Typography variant="h6">{hood.name}</Typography>}
                action={hood.type === 'new' ?
                    <Chip label="New" size="small" color="primary" variant="outlined"/> : undefined}
            />
            <Divider/>
            <CardContent>
                <Stack spacing={2} divider={<Divider flexItem/>}>{changes.map(change => (
                    <ChangeRow
                        key={change.key}
                        icon={change.icon}
                        revert={change.revert}
                    >
                        {change.content}
                    </ChangeRow>
                ))}</Stack>
            </CardContent>
        </Card>
    );
}

function SummaryTab({model}: { model: SeatingPlanModel }) {
    const {canAdmin} = usePermission({canAdmin: SeatingPlans_Admin});
    const confirm = useConfirm();
    const message = useMessage();

    const changes = useMemo(() =>
            collectChanges(model, new RevertContext(message, confirm, canAdmin)),
        [model, canAdmin, confirm, message],
    );

    return changes.length > 0 ? (
        <Masonry columns={{xs: 1, md: 2, xl: 3}} spacing={2}>
            {changes.map(({hood, changes}) => (
                <NeighbourhoodCard
                    key={hood.key}
                    hood={hood}
                    changes={changes}
                    canAdmin={canAdmin}
                />
            ))}
        </Masonry>
    ) : (
        <Alert color="info">No changes planned yet</Alert>
    );
}

export const summaryTab: SeatingPlanTab<'newNeighbourhoodAction' | 'editPlanAction' | 'applyPlanAction'> = {
    label: (_, active) => (
        <SeatingPlanTabLabel label="Summary" active={active}/>
    ),
    key: 'summary',
    actions: {
        newNeighbourhoodAction,
        editPlanAction,
        applyPlanAction,
    },
    content: SummaryTab,
};
